import { ENDPOINTS, callApi } from 'utils/api/callApi'
import {
  Parser,
  ParserConfigs,
  parserVersionRequest,
  parserResponse,
  ParserVersion,
  runParserRequest,
  parserRequest,
  ParserTest,
  ParserTestRequest,
} from '../types'

/**
 * Gets all Inbound Parsers
 * @method GET
 */
export const getParsers = (): Promise<Parser[]> => {
  return callApi({
    method: 'GET',
    endpoint: ENDPOINTS.ediSelfServeParsers,
  })
}

/**
 * Gets all Inbound Parsers
 * @method GET
 */
export const getParserVersions = (): Promise<ParserVersion[]> => {
  return callApi(
    {
      method: 'GET',
      endpoint: ENDPOINTS.ediSelfServeParserVersions,
    },
    { shouldCamelizeKeys: false }
  )
}

/**
 * Gets all Parser Config Options
 * @method GET
 */
export const getParserConfigs = (): Promise<ParserConfigs> => {
  return callApi({
    endpoint: ENDPOINTS.ediSelfServeConfigs,
    method: 'GET',
  })
}

/**
 * Updates a Parser Version
 * @method PUT
 */
export const updateParserVersion = (
  body: parserVersionRequest,
  parserVersion?: ParserVersion
): Promise<ParserVersion> => {
  return callApi(
    {
      endpoint: ENDPOINTS.ediSelfServeParserVersions,
      method: 'PUT',
      body,
      id: parserVersion?.id,
    },
    { shouldCamelizeKeys: false, shouldFormatRequestObj: false }
  )
}

/**
 * Runs a particular parser version
 * @method POST
 */
export const runParserVersion = (body: runParserRequest): Promise<parserResponse> => {
  return callApi(
    {
      endpoint: ENDPOINTS.ediSelfServeRunParser,
      method: 'POST',
      body,
    },
    { shouldCamelizeKeys: false }
  )
}

/**
 * Creates a particular parser version
 * @method POST
 */
export const createParserVersion = (body: parserVersionRequest): Promise<ParserVersion> => {
  return callApi(
    {
      endpoint: ENDPOINTS.ediSelfServeParserVersions,
      method: 'POST',
      body,
    },
    { shouldCamelizeKeys: false, shouldFormatRequestObj: false }
  )
}

/**
 * Creates a particular parser version
 * @method POST
 */
export const createParser = (body: parserRequest): Promise<Parser> => {
  return callApi({
    endpoint: ENDPOINTS.ediSelfServeParsers,
    method: 'POST',
    body,
  })
}

/**
 * Updates a particular parser version
 * @method PUT
 */
export const updateParser = (body: parserRequest, id: number): Promise<Parser> => {
  return callApi({
    endpoint: ENDPOINTS.ediSelfServeParsers,
    method: 'PUT',
    body,
    id,
  })
}

/**
 * Deletes a particular parser version
 * @method DELETE
 */
export const deleteParserVersion = (parserVersionId: number): Promise<ParserVersion> => {
  return callApi({
    endpoint: ENDPOINTS.ediSelfServeParserVersions,
    method: 'DELETE',
    id: parserVersionId,
  })
}

/**
 * Makes a particular parser version live
 * @method POST
 */
export const makeParserVersionLive = (
  body: parserVersionRequest,
  parserVersionId: number
): Promise<ParserVersion> => {
  return callApi({
    endpoint: ENDPOINTS.ediSelfServeParserVersions,
    method: 'POST',
    body,
    group: `${parserVersionId}/make_live`,
  })
}

export const deleteParser = (parserVersionId: number) => {
  return callApi({
    endpoint: ENDPOINTS.ediSelfServeParserVersions,
    method: 'DELETE',
    id: parserVersionId,
  })
}

/**
 * Gets all particular parser tests
 * @method GET
 */
export const getParserTests = async (): Promise<ParserTest[]> => {
  return callApi(
    {
      endpoint: ENDPOINTS.ediSelfServeTests,
      method: 'GET',
    },
    { shouldCamelizeKeys: false }
  )
}

/**
 * Deletes a parser test
 * @method DELETE
 */
export const deleteParserTest = async (parserTestId: number): Promise<ParserTest> => {
  return callApi({
    endpoint: ENDPOINTS.ediSelfServeTests,
    method: 'DELETE',
    id: parserTestId,
  })
}

/**
 * Creates a parser test
 * @method POST
 */
export const createParserTest = async (body: ParserTestRequest): Promise<ParserTest> => {
  return callApi({
    endpoint: ENDPOINTS.ediSelfServeTests,
    method: 'POST',
    body,
  })
}

/**
 * Runs all parser tests associated with the specified Parser Version.
 * @method POST
 */
export const runParserTests = async (currentParserVersion: ParserVersion) => {
  return callApi(
    {
      endpoint: ENDPOINTS.ediSelfServeParserVersions,
      method: 'POST',
      group: `${currentParserVersion.id}/test`,
    },
    { shouldCamelizeKeys: false }
  )
}

/**
 * Updates all parser tests with the info from the current version.
 * @method POST
 */
export const updateParserTests = async (currentParserVersion: ParserVersion) => {
  return callApi(
    {
      endpoint: ENDPOINTS.ediSelfServeParserVersions,
      method: 'POST',
      group: `${currentParserVersion.id}/update_tests`,
    },
    { shouldCamelizeKeys: false }
  )
}

/**
 * Updates a parser test
 * @method POST
 */
export const updateParserTest = async (
  body: ParserTestRequest,
  parserTest: ParserTest
): Promise<ParserTest> => {
  return callApi({
    endpoint: ENDPOINTS.ediSelfServeTests,
    method: 'PUT',
    body,
    id: parserTest?.id,
  })
}
